import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import api from "@/lib/api";

const money = (paise) => `₹${Math.round((paise || 0) / 100).toLocaleString("en-IN")}`;

function Row({ level, id, name, spend, purchases, revenue, actualRevenue, actualDonors, expandable, expanded, onToggle, spendMissing }) {
  const metaRoas = spend ? (revenue / spend).toFixed(2) : "—";
  const realRoas = spend && actualRevenue != null ? (actualRevenue / spend).toFixed(2) : "—";
  return (
    <tr className={level === 0 ? "font-semibold" : level === 1 ? "text-sm" : "text-xs text-amtm-textMuted"} data-testid={`performance-row-${id}`}>
      <td className="p-2" style={{ paddingLeft: `${level * 20 + 8}px` }}>
        {expandable ? (
          <button type="button" onClick={onToggle} className="flex items-center gap-1" data-testid={`performance-toggle-${id}`}>
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} {name}
          </button>
        ) : (
          name
        )}
      </td>
      <td className="p-2">{spendMissing ? "—" : money(spend)}</td>
      <td className="p-2">{spendMissing ? "—" : purchases}</td>
      <td className="p-2">{spendMissing ? "—" : money(revenue)}</td>
      <td className="p-2">{actualRevenue != null ? money(actualRevenue) : "—"}</td>
      <td className="p-2">{actualDonors ?? "—"}</td>
      {spendMissing ? (
        <td className="p-2 text-amtm-textMuted" colSpan={2} data-testid={`performance-spend-missing-${id}`}>spend missing</td>
      ) : (
        <>
          <td className="p-2">{metaRoas}x</td>
          <td className="p-2 font-semibold text-amtm-teal">{realRoas}x</td>
        </>
      )}
    </tr>
  );
}

export default function PerformanceBreakdownTable({ range }) {
  const [breakdown, setBreakdown] = useState(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [expandedAdsets, setExpandedAdsets] = useState({});

  useEffect(() => {
    api.get("/admin/performance/summary", { params: range }).then(({ data }) => setBreakdown(data.breakdown));
  }, [range]);

  if (!breakdown) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>;
  }

  return (
    <div className="bg-amtm-surface border border-amtm-border rounded-xl overflow-x-auto mb-6" data-testid="performance-breakdown-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-amtm-textMuted border-b border-amtm-border">
            <th className="p-2">Campaign / Ad Set / Ad</th>
            <th className="p-2">Spend</th>
            <th className="p-2">Meta Purchases</th>
            <th className="p-2">Meta Revenue</th>
            <th className="p-2">Actual Revenue</th>
            <th className="p-2">Actual Donors</th>
            <th className="p-2">Meta ROAS</th>
            <th className="p-2">Real ROAS</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-amtm-border">
          {breakdown.length === 0 && (
            <tr>
              <td colSpan={8} className="p-6 text-center text-amtm-textMuted" data-testid="performance-breakdown-empty">
                No ad spend data for this range yet — run a Meta Sync or upload a CSV export.
              </td>
            </tr>
          )}
          {breakdown.map((camp) => (
            <Fragment key={camp.campaign_id}>
              <Row
                level={0}
                id={camp.campaign_id}
                name={camp.campaign_name}
                spend={camp.spend_paise}
                purchases={camp.meta_purchases}
                revenue={camp.meta_revenue_paise}
                actualRevenue={camp.actual_revenue_paise}
                actualDonors={camp.actual_donor_count}
                spendMissing={camp.spend_missing}
                expandable={camp.adsets.length > 0}
                expanded={!!expandedCampaigns[camp.campaign_id]}
                onToggle={() => setExpandedCampaigns((s) => ({ ...s, [camp.campaign_id]: !s[camp.campaign_id] }))}
              />
              {expandedCampaigns[camp.campaign_id] &&
                camp.adsets.map((adset) => (
                  <Fragment key={adset.adset_id}>
                    <Row
                      level={1}
                      id={adset.adset_id}
                      name={adset.adset_name}
                      spend={adset.spend_paise}
                      purchases={adset.meta_purchases}
                      revenue={adset.meta_revenue_paise}
                      expandable
                      expanded={!!expandedAdsets[adset.adset_id]}
                      onToggle={() => setExpandedAdsets((s) => ({ ...s, [adset.adset_id]: !s[adset.adset_id] }))}
                    />
                    {expandedAdsets[adset.adset_id] &&
                      adset.ads.map((ad) => (
                        <Row key={ad.ad_id} level={2} id={ad.ad_id} name={ad.ad_name} spend={ad.spend_paise} purchases={ad.meta_purchases} revenue={ad.meta_revenue_paise} />
                      ))}
                  </Fragment>
                ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
